/* PDP enhancements use the theme's existing variant, media and cart handlers. */
(() => {
  const motion = () => matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

  function init(section) {
    if (section.dataset.cherryReady) return;
    section.dataset.cherryReady = 'true';
    const slider = section.querySelector('[data-product-single-media-slider]');
    const thumbs = section.querySelector('[data-cherry-thumbnails]');
    const progress = section.querySelector('[data-cherry-gallery-progress]');
    const shopLook = section.querySelector('[data-cherry-shop-look]');
    const flickity = () => window.themeVendor?.Flickity?.data(slider);

    function updateShopLook() {
      if (!shopLook) return;
      const selectedMedia = slider?.querySelector('.is-selected[data-media-id]') || slider?.querySelector('[data-media-id]:not(.media--hidden)');
      shopLook.hidden = !selectedMedia?.hasAttribute('data-shop-look-image');
    }

    const placeKiwiSizeChart = () => {
      const sizeLabel = section.querySelector('.cherry-product__size-label');
      const chart = section.querySelector('.ks-chart-container.sizing-chart-container');
      if (!sizeLabel || !chart) return;
      if (chart.parentElement !== sizeLabel) sizeLabel.append(chart);
      if (chart.querySelector('.ks-chart-modal-link')) {
        section.classList.add('kiwi-size-chart-ready');
        section.classList.remove('kiwi-size-chart-fallback');
      }
    };
    const kiwiObserver = new MutationObserver(placeKiwiSizeChart);
    kiwiObserver.observe(section, { childList: true, subtree: true });
    placeKiwiSizeChart();
    const kiwiFallbackTimer = window.setTimeout(() => {
      if (!section.classList.contains('kiwi-size-chart-ready')) {
        section.classList.add('kiwi-size-chart-fallback');
      }
    }, 5000);

    function syncThumbs() {
      const selected = slider?.querySelector('.is-selected[data-media-id]') || slider?.querySelector('[data-media-id]:not(.media--hidden)');
      const slides = [...(slider?.querySelectorAll('[data-media-id]') || [])];
      const count = Math.max(slides.length, 1);
      const index = Math.max(slides.indexOf(selected), 0);
      if (progress) {
        progress.style.setProperty('--gallery-count', count);
        progress.style.setProperty('--gallery-index', index);
        progress.setAttribute('aria-valuemax', count);
        progress.setAttribute('aria-valuenow', index + 1);
        progress.setAttribute('aria-valuetext', `Image ${index + 1} of ${count}`);
      }
      thumbs?.querySelectorAll('[data-thumbnail-id]').forEach(thumb => {
        const active = thumb.dataset.thumbnailId === selected?.dataset.mediaId;
        thumb.classList.toggle('is-nav-selected', active);
        const link = thumb.querySelector('a');
        link?.setAttribute('aria-current', String(active));
      });
      updateShopLook();
    }
    thumbs?.addEventListener('click', event => {
      const thumb = event.target.closest('[data-thumbnail-id]');
      if (!thumb) return;
      event.preventDefault();
      const carousel = flickity();
      const index = carousel?.cells.findIndex(cell => cell.element.dataset.mediaId === thumb.dataset.thumbnailId);
      if (index >= 0) carousel.select(index);
      syncThumbs();
    });
    const observer = new MutationObserver(syncThumbs);
    if (slider) observer.observe(slider, { subtree: true, attributes: true, attributeFilter: ['class'] });
    syncThumbs();

    const product = JSON.parse(section.querySelector('[data-product-json]')?.textContent || '{}');
    let currentVariant = product.variants?.find(variant => String(variant.id) === section.querySelector('[data-product-select]')?.value);
    let backInStockVariant = null;

    function getBackInStockForm(picker) {
      let form = section.querySelector('[data-cherry-bis-form]');
      if (form) return form;

      form = document.createElement('form');
      form.className = 'cherry-product__bis-form';
      form.hidden = true;
      form.setAttribute('data-cherry-bis-form', '');
      form.innerHTML = `
        <p class="cherry-product__bis-title">We’ll Notify You When This Size Is Back In Stock</p>
        <label class="visually-hidden" for="CherryBisEmail-${section.dataset.sectionId || 'product'}">Your email</label>
        <input class="cherry-product__bis-email" id="CherryBisEmail-${section.dataset.sectionId || 'product'}" type="email" inputmode="email" autocomplete="email" placeholder="Your email" required>
        <label class="cherry-product__bis-marketing">
          <input type="checkbox" data-cherry-bis-marketing checked>
          <span>Join our newsletter</span>
        </label>
        <button class="cherry-product__bis-submit" type="submit">Submit</button>
        <p class="cherry-product__bis-message" data-cherry-bis-message role="status" hidden></p>`;
      picker.insertAdjacentElement('afterend', form);

      form.addEventListener('submit', event => {
        event.preventDefault();
        const email = form.querySelector('.cherry-product__bis-email');
        const submit = form.querySelector('.cherry-product__bis-submit');
        const message = form.querySelector('[data-cherry-bis-message]');
        if (!email.reportValidity() || !backInStockVariant) return;
        if (!window.BIS?.create) {
          message.textContent = 'Stock notifications are temporarily unavailable. Please try again shortly.';
          message.hidden = false;
          return;
        }

        submit.disabled = true;
        message.hidden = true;
        const acceptsMarketing = form.querySelector('[data-cherry-bis-marketing]').checked;
        window.BIS.create(email.value, backInStockVariant.id, product.id, {
          accepts_marketing: acceptsMarketing
        }).then(response => {
          message.textContent = response?.message || (response?.status === 'OK' ? 'You’re on the list.' : 'Please try again.');
          message.hidden = false;
          submit.disabled = false;
          if (response?.status === 'OK') form.reset();
        });
      });
      return form;
    }

    function showBackInStockForm(picker, variant) {
      backInStockVariant = variant;
      const form = getBackInStockForm(picker);
      form.hidden = false;
      form.querySelector('[data-cherry-bis-message]').hidden = true;
    }

    function hideBackInStockForm() {
      backInStockVariant = null;
      const form = section.querySelector('[data-cherry-bis-form]');
      if (form) form.hidden = true;
    }
    const delivery = section.querySelector('[data-cherry-delivery]');
    const deliveryOptions = [...section.querySelectorAll('[data-cherry-delivery-option]')];
    const pickup = section.querySelector('[data-cherry-pickup]');
    const pickupStatus = section.querySelector('[data-cherry-pickup-status]');
    const pickupRetry = section.querySelector('[data-cherry-pickup-retry]');
    const pickupLocations = section.querySelector('[data-cherry-pickup-locations]');
    const shipOption = deliveryOptions.find(input => input.value === 'Ship');
    const pickupOption = deliveryOptions.find(input => input.value === 'Pick up in store');
    const pickupLabel = pickupOption?.closest('.cherry-product__delivery-option');
    const availabilityCache = new Map();
    let pickupRequest;
    let pickupLocationId;

    function updatePickupCartState() {
      const button = section.querySelector('[data-add-to-cart]');
      if (!button || !pickup) return;
      const blocked = !pickup.hidden && (pickup.hasAttribute('aria-busy') || !pickupLocations.querySelector('[data-cherry-pickup-location]:checked:not(:disabled)'));
      button.toggleAttribute('data-cherry-pickup-blocked', blocked);
      button.disabled = !currentVariant?.available || blocked;
    }

    async function fetchPickupAvailability(variantId, signal) {
      if (availabilityCache.has(variantId)) {
        return availabilityCache.get(variantId);
      }
      const root = window.Shopify?.routes?.root || '/';
      const url = new URL(`${root}variants/${variantId}/`, window.location.origin);
      url.searchParams.set('section_id', 'store-availability');
      const previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
      if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);

      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error('Pickup availability unavailable');
      const html = await response.text();
      const content = new DOMParser().parseFromString(html, 'text/html');
      const availability = content.querySelector('[data-cherry-pickup-state]');
      if (!availability || availability.dataset.cherryPickupState === 'error' || (availability.dataset.cherryPickupVariant && availability.dataset.cherryPickupVariant !== String(variantId))) {
        throw new Error('Invalid pickup availability response');
      }
      if (availability.dataset.cherryPickupState === 'no-locations') {
        const result = { available: false, cardsHtml: '' };
        availabilityCache.set(variantId, result);
        return result;
      }
      const locations = content.querySelector('template[data-cherry-pickup-cards]');
      const availableCards = locations?.content ?
        [...locations.content.querySelectorAll('[data-cherry-pickup-location]:not(:disabled)')] :
        [...content.querySelectorAll('[data-cherry-pickup-location]:not(:disabled)')];
      const result = {
        available: availableCards.length > 0,
        cardsHtml: locations ? locations.innerHTML : ''
      };
      availabilityCache.set(variantId, result);
      return result;
    }

    async function updateDelivery() {
      if (!delivery) return;
      const sizes = [...section.querySelectorAll('[data-cherry-size]')];
      const isReady = Boolean(currentVariant && sizes.every(select => select.value));
      delivery.hidden = !isReady;
      if (!isReady) {
        deliveryOptions.forEach(input => { input.disabled = true; });
        pickupLabel?.classList.remove('is-disabled');
        pickupLabel?.removeAttribute('title');
        pickup.hidden = true;
        pickupLocations.replaceChildren();
        updatePickupCartState();
        return;
      }

      if (shipOption) shipOption.disabled = false;

      const variantId = currentVariant.id;
      pickupRetry.hidden = true;

      pickupRequest?.abort();
      const controller = new AbortController();
      pickupRequest = controller;

      // Show busy state on pickup if currently requested
      if (pickupOption?.checked) {
        pickup.setAttribute('aria-busy', 'true');
        pickupStatus.textContent = 'Checking pickup availability…';
      }

      try {
        const info = await fetchPickupAvailability(variantId, controller.signal);
        if (controller.signal.aborted) return;

        if (info.available) {
          // Pickup is eligible and available
          if (pickupOption) pickupOption.disabled = false;
          pickupLabel?.classList.remove('is-disabled');
          pickupLabel?.removeAttribute('title');

          const wantsPickup = Boolean(pickupOption?.checked);
          pickup.hidden = !wantsPickup;

          if (wantsPickup) {
            pickupLocations.innerHTML = info.cardsHtml;
            const available = [...pickupLocations.querySelectorAll('[data-cherry-pickup-location]:not(:disabled)')];
            const selected = available.find(input => input.dataset.cherryPickupLocation === pickupLocationId) || available[0];
            if (selected) {
              selected.checked = true;
              pickupLocationId = selected.dataset.cherryPickupLocation;
            }
            pickupStatus.textContent = selected ? '' : 'Pickup is not available for this size and color.';
          } else {
            pickupLocations.replaceChildren();
            pickupStatus.textContent = '';
          }
        } else {
          // Pickup is not offered or not available for this variant
          if (pickupOption) pickupOption.disabled = true;
          pickupLabel?.classList.add('is-disabled');
          pickupLabel?.setAttribute('title', 'Pickup is unavailable for this item');

          // If pickup was checked, switch back to Ship
          if (pickupOption?.checked && shipOption) {
            shipOption.checked = true;
            pickupOption.checked = false;
          }
          pickup.hidden = true;
          pickupLocations.replaceChildren();
          pickupStatus.textContent = '';
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          if (pickupOption) pickupOption.disabled = true;
          pickupLabel?.classList.add('is-disabled');
          pickupLabel?.setAttribute('title', 'Unable to check pickup availability');
          if (pickupOption?.checked && shipOption) {
            shipOption.checked = true;
            pickupOption.checked = false;
            pickup.hidden = true;
          }
          pickupRetry.hidden = false;
          pickupStatus.textContent = 'Unable to check pickup availability. Please try again.';
        }
      } finally {
        if (!controller.signal.aborted) {
          pickup.removeAttribute('aria-busy');
          updateShopLook();
          updatePickupCartState();
        }
      }
    }

    pickupRetry?.addEventListener('click', () => {
      if (currentVariant?.id) availabilityCache.delete(currentVariant.id);
      updateDelivery();
    });
    pickupLocations?.addEventListener('change', event => {
      if (event.target.matches('[data-cherry-pickup-location]')) {
        pickupLocationId = event.target.dataset.cherryPickupLocation;
        updatePickupCartState();
      }
    });
    deliveryOptions.forEach(input => input.addEventListener('change', updateDelivery));
    section.addEventListener('cherry:cleanup', () => pickupRequest?.abort(), { once: true });
    const sizePickers = [];
    section.querySelectorAll('[data-cherry-size]').forEach(select => {
      const picker = select.closest('[data-cherry-size-picker]');
      const input = picker.querySelector('[data-cherry-size-value]');
      const toggle = picker.querySelector('[data-cherry-size-toggle]');
      const menu = picker.querySelector('[data-cherry-size-menu]');
      const message = picker.querySelector('[data-cherry-size-message]');
      const rows = [...picker.querySelectorAll('[data-cherry-size-option]')];
      const sizeIndex = Number(input.dataset.index.replace('option', '')) - 1;
      const enabledRows = () => rows.filter(row => !row.disabled);
      const close = (restoreFocus = false) => {
        menu.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
        if (restoreFocus) toggle.focus();
      };
      const open = (keyboard = false) => {
        menu.hidden = false;
        toggle.setAttribute('aria-expanded', 'true');
        if (keyboard) {
          const enabled = enabledRows();
          (keyboard === 'last' ? enabled[enabled.length - 1] : enabled.find(row => row.dataset.cherrySizeOption === select.value) || enabled[0])?.focus();
        }
      };
      const variantFor = value => {
        const options = [...(currentVariant?.options || product.variants?.[0]?.options || [])];
        section.querySelectorAll('[data-single-option-selector]').forEach(control => {
          if (['radio', 'checkbox'].includes(control.type) && !control.checked) return;
          const index = Number(control.dataset.index?.replace('option', '')) - 1;
          if (index >= 0) options[index] = control.value;
        });
        options[sizeIndex] = value;
        return product.variants?.find(variant => variant.options.every((option, index) => option === options[index]));
      };
      const sync = () => {
        toggle.querySelector('[data-cherry-size-text]').textContent = select.value || 'Select Size';
        rows.forEach(row => {
          const variant = variantFor(row.dataset.cherrySizeOption);
          const soldOut = Boolean(variant && !variant.available);
          row.disabled = !variant;
          row.classList.toggle('is-sold-out', soldOut);
          row.querySelector('[data-cherry-size-notify]').hidden = !soldOut;
          row.setAttribute('aria-pressed', String(row.dataset.cherrySizeOption === select.value));
          row.setAttribute('aria-label', `${row.dataset.cherrySizeOption}${soldOut ? ', sold out, notify me' : !variant ? ', unavailable' : ''}`);
        });
      };
      // Keep the native select as the required form control and no-JS fallback.
      select.hidden = true;
      toggle.hidden = false;
      const label = section.querySelector(`#${CSS.escape(select.id)}-label`);
      toggle.id = `${select.id}-toggle`;
      label?.setAttribute('for', toggle.id);
      select.addEventListener('invalid', event => {
        event.preventDefault();
        toggle.setAttribute('aria-invalid', 'true');
        open();
      });
      toggle.addEventListener('click', () => menu.hidden ? open(false) : close());
      select.addEventListener('change', () => {
        input.value = select.value;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        toggle.removeAttribute('aria-invalid');
        message.hidden = true;
        sync();
        close(false);
        updateDelivery();
      });
      const selectSize = (optionValue) => {
        if (!optionValue) return;
        const variant = variantFor(optionValue);
        if (variant) currentVariant = variant;
        select.value = optionValue;
        input.value = optionValue;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
        toggle.removeAttribute('aria-invalid');
        message.hidden = true;
        sync();
        close(false);
        updateDelivery();
        if (variant && !variant.available) {
          showBackInStockForm(picker, variant);
        } else hideBackInStockForm();
      };
      menu.addEventListener('click', event => {
        const row = event.target.closest('[data-cherry-size-option]');
        if (!row || row.disabled) return;
        selectSize(row.dataset.cherrySizeOption);
      });
      picker.addEventListener('keydown', event => {
        if (event.key === 'Escape') { event.preventDefault(); close(true); return; }
        if (event.key === 'Tab') { close(false); return; }
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (menu.hidden) { open(event.key === 'ArrowUp' || event.key === 'End' ? 'last' : true); return; }
        const enabled = enabledRows();
        const index = enabled.indexOf(document.activeElement);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? enabled.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + enabled.length) % enabled.length;
        enabled[next]?.focus();
      });
      picker.addEventListener('focusout', event => {
        if (!event.relatedTarget || event.relatedTarget === document.body || event.relatedTarget === document.documentElement) return;
        if (picker.contains(event.relatedTarget)) return;
        requestAnimationFrame(() => {
          if (document.activeElement && !picker.contains(document.activeElement)) close();
        });
      });
      const outsideClick = event => { if (!picker.contains(event.target)) close(); };
      document.addEventListener('click', outsideClick);
      document.addEventListener('pointerdown', outsideClick);
      section.addEventListener('cherry:cleanup', () => {
        document.removeEventListener('click', outsideClick);
        document.removeEventListener('pointerdown', outsideClick);
      }, { once: true });
      sizePickers.push(sync);
      sync();
    });
    const colorLabels = [];
    section.querySelectorAll('.product__form .selector-wrapper--swatches, .product__form .product-color-').forEach(wrapper => {
      const linked = wrapper.classList.contains('product-color-');
      const label = wrapper.querySelector(linked ? ':scope > span' : '.radio__legend__label');
      if (!label) return;
      const initialColor = label.textContent.trim();
      if (!linked) label.dataset.optionLabel ||= initialColor;
      let hovered = null;
      let focused = null;
      const colorName = swatch => linked
        ? swatch?.querySelector('img')?.alt
        : swatch?.querySelector('.swatch__input')?.value;
      const sync = () => {
        const selected = linked
          ? wrapper.querySelector('.colordropdown li.selected')
          : wrapper.querySelector('.swatch__input:checked')?.closest('.swatch__button');
        const color = colorName(hovered || focused || selected) || (linked ? initialColor : '');
        label.textContent = linked ? color : label.dataset.optionLabel + (color ? ': ' + color : '');
      };
      wrapper.querySelectorAll(linked ? '.colordropdown li' : '.swatch__button').forEach(swatch => {
        swatch.addEventListener('pointerenter', event => {
          if (event.pointerType === 'touch') return;
          hovered = swatch;
          sync();
        });
        swatch.addEventListener('pointerleave', () => {
          hovered = null;
          sync();
        });
        swatch.addEventListener('focusin', () => {
          focused = swatch;
          sync();
        });
        swatch.addEventListener('focusout', () => {
          focused = null;
          sync();
        });
      });
      colorLabels.push(sync);
    });

    function updateForm() {
      sizePickers.forEach(sync => sync());
      updateDelivery();
      const sizePicker = section.querySelector('[data-cherry-size-picker]');
      const backInStockAnchor = sizePicker || section.querySelector('.product__submit__holder');
      if (backInStockAnchor && currentVariant && !currentVariant.available) {
        showBackInStockForm(backInStockAnchor, currentVariant);
      } else {
        hideBackInStockForm();
      }
      const label = section.querySelector('[data-add-to-cart-text]');
      if (label) {
        label.dataset.soldOut = String(Boolean(currentVariant && !currentVariant.available));
        if (currentVariant?.available && !product.tags?.includes('_preorder')) label.textContent = 'Add to bag';
        const button = section.querySelector('[data-add-to-cart]');
        if (button && currentVariant && !currentVariant.available) button.disabled = true;
      }
      colorLabels.forEach(sync => sync());
    }
    updateForm();
    section.addEventListener('theme:variant:change', event => {
      currentVariant = event.detail.variant;
      requestAnimationFrame(updateForm);
    });
    // The theme submits AJAX cart requests on click, before native form validation.
    section.addEventListener('click', event => {
      const button = event.target.closest('[data-add-to-cart]');
      const form = button?.closest('form');
      if (form && !form.checkValidity()) {
        event.preventDefault();
        event.stopPropagation();
        form.reportValidity();
      }
    }, true);

    function setupPairs() {
      section.querySelectorAll('[data-cherry-pair]').forEach(pair => {
        if (pair.dataset.ready) return;
        pair.dataset.ready = 'true';
        const track = pair.querySelector('[data-cherry-pair-track]');
        const prev = pair.querySelector('[data-cherry-pair-prev]');
        const next = pair.querySelector('[data-cherry-pair-next]');
        const controls = pair.querySelector('.cherry-product__pair-controls');
        const update = () => {
          controls.hidden = track.scrollWidth <= track.clientWidth + 1;
          prev.disabled = track.scrollLeft < 1;
          next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
        };
        prev.addEventListener('click', () => track.scrollBy({ left: -track.clientWidth, behavior: motion() }));
        next.addEventListener('click', () => track.scrollBy({ left: track.clientWidth, behavior: motion() }));
        track.addEventListener('scroll', update, { passive: true });
        const resize = new ResizeObserver(update);
        resize.observe(track);
        section.addEventListener('cherry:cleanup', () => resize.disconnect(), { once: true });
        update();
      });
      updateShopLook();
    }
    setupPairs();
    const recommendations = section.querySelector('[data-complementary-products]');
    const pairObserver = new MutationObserver(setupPairs);
    if (recommendations) pairObserver.observe(recommendations, { childList: true, subtree: true });
    section.querySelector('[data-cherry-dismiss]')?.addEventListener('click', event => {
      const promo = event.currentTarget.closest('[data-cherry-promo]');
      promo.hidden = true;
      section.querySelector('[data-cherry-pair-track]')?.focus();
    });
    section.addEventListener('cherry:cleanup', () => {
      observer.disconnect();
      pairObserver.disconnect();
      kiwiObserver.disconnect();
      window.clearTimeout(kiwiFallbackTimer);
    }, { once: true });
  }

  function scan(root) { root.querySelectorAll('[data-cherry-product]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document));
  else scan(document);
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-cherry-product]').forEach(section => section.dispatchEvent(new Event('cherry:cleanup')));
  });
})();
